import React, { useState, useRef, useEffect } from 'react';
import {
  MapPinIcon, EyeSlashIcon, ArrowRightIcon,
  ChatBubbleLeftRightIcon, ClockIcon,
  GlobeAmericasIcon, Bars3Icon, FunnelIcon, Squares2X2Icon,
  XMarkIcon, SpeakerWaveIcon
} from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';

// --- HELPER: EXTRACT SINGLE BEST KEYWORD ---
const getSingleSmartKeyword = (title, category) => {
    if (!title) return category || 'news';
    
    // 1. Common words to ignore (Stop words)
    const stopWords = new Set([
        'the', 'is', 'at', 'which', 'on', 'and', 'a', 'an', 'in', 'to', 'of', 
        'for', 'with', 'by', 'should', 'could', 'would', 'be', 'it', 'that', 
        'this', 'are', 'from', 'as', 'but', 'or', 'not', 'what', 'why', 'how',
        'does', 'will', 'can', 'has', 'have', 'do', 'did', 'new', 'top', 'daily',
        'us', 'uk', 'vs', 'over', 'after', 'before', 'under', 'about', 'their'
    ]);

    // 2. Clean and split title
    const words = title.toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .split(/\s+/)            // Split by whitespace
        .filter(w => !stopWords.has(w) && w.length > 3); // Remove stop words & short words

    // 3. Strategy: "ONE WORD"
    // Pick the LONGEST word. In English, the longest word in a headline 
    // is usually the most specific subject (e.g., "Cryptocurrency", "Inflation", "Legislature").
    if (words.length > 0) {
        // Sort by length (descending) -> take the first
        words.sort((a, b) => b.length - a.length);
        return words[0]; 
    }

    // Fallback if no valid words found
    return category || 'news';
};

// --- SINGLE REEL CARD COMPONENT ---
const ReelCard = ({ 
    topic, 
    onTopicClick, 
    onPin, 
    onHide, 
    isPinned,
    shouldLoad // Lazy load trigger
}) => {
    const [offset, setOffset] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const [imgSrc, setImgSrc] = useState(null);
    const [imgError, setImgError] = useState(false);

    // Audio state
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    // Touch tracking refs
    const startX = useRef(0);
    const startY = useRef(0);
    
    const ACTION_THRESHOLD = 100; // Distance to trigger action

    // Initialize Image
    useEffect(() => {
        if (!shouldLoad) return;

        setOffset(0);
        setImgError(false);

        // Reset audio when switching cards
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);

        // Helper to validate image URL
        const isValidImageUrl = (url) => {
            if (!url || typeof url !== 'string') return false;
            const trimmed = url.trim();
            return trimmed.length > 0 && trimmed.startsWith('http');
        };

        // Priority: 1. RSS Image -> 2. Legacy Field -> 3. One-Word Contextual Fallback
        const rssImage = topic.image;
        const legacyImage1 = topic.urlToImage;
        const legacyImage2 = topic.imageUrl;

        if (isValidImageUrl(rssImage)) {
            setImgSrc(rssImage);
        } else if (isValidImageUrl(legacyImage1)) {
            setImgSrc(legacyImage1);
        } else if (isValidImageUrl(legacyImage2)) {
            setImgSrc(legacyImage2);
        } else {
            // No valid RSS image - use contextual fallback
            const keyword = getSingleSmartKeyword(topic.title, topic.category);
            const uniqueLock = (topic.id || Math.random().toString()).split('').reduce((a,b) => a + b.charCodeAt(0), 0);
            setImgSrc(`https://loremflickr.com/800/1200/${keyword}?lock=${uniqueLock}`);
        }
    }, [topic, shouldLoad]);

    const handleImgError = () => {
        // On error, try fallback to loremflickr if we were using an RSS image
        if (imgSrc && !imgSrc.includes('loremflickr.com')) {
            const keyword = getSingleSmartKeyword(topic.title, topic.category);
            const uniqueLock = (topic.id || Math.random().toString()).split('').reduce((a,b) => a + b.charCodeAt(0), 0);
            setImgSrc(`https://loremflickr.com/800/1200/${keyword}?lock=${uniqueLock}`);
        } else {
            // If even loremflickr fails, show the pattern fallback
            setImgError(true);
        }
    };

    const handleTouchStart = (e) => {
        startX.current = e.touches[0].clientX;
        startY.current = e.touches[0].clientY;
        setIsSwiping(true);
    };

    const handleTouchMove = (e) => {
        if (!isSwiping) return;
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = currentX - startX.current;
        const diffY = currentY - startY.current;

        if (Math.abs(diffX) > Math.abs(diffY)) {
            if (e.cancelable && Math.abs(diffX) > 10) {
                e.preventDefault(); 
            }
            if (diffX > 0) {
                setOffset(Math.min(diffX, 200)); 
            } else {
                setOffset(Math.max(diffX, -200)); 
            }
        }
    };

    const handleTouchEnd = () => {
        setIsSwiping(false);
        
        if (offset > ACTION_THRESHOLD) {
            onPin(topic.id);
            setOffset(0); 
        } else if (offset < -ACTION_THRESHOLD) {
            onHide(topic.id);
            setOffset(-500); 
        } else {
            setOffset(0);
        }
    };

    const handleClick = (e) => {
        if (Math.abs(offset) > 5) return;
        onTopicClick(e, topic);
    };

    // --- VISUAL CALCULATIONS ---
    const pinOpacity = offset > 0 ? Math.min(1, offset / 100) : 0;
    const hideOpacity = offset < 0 ? Math.min(1, Math.abs(offset) / 100) : 0;
    const scale = 1 - Math.min(0.05, Math.abs(offset) / 1000);
    const rotate = offset / 40; 

    const getGradientColors = () => {
        const cat = (topic.category || 'general').toLowerCase();
        if (cat.includes('politics')) return 'from-blue-900 to-slate-900';
        if (cat.includes('tech')) return 'from-indigo-900 to-slate-900';
        if (cat.includes('env')) return 'from-emerald-900 to-slate-900';
        if (cat.includes('econ')) return 'from-slate-800 to-gray-900';
        return 'from-slate-800 to-slate-950'; 
    };

    return (
        <div className="relative w-full h-full snap-start flex-shrink-0 overflow-hidden bg-black">
            
            {/* BACKGROUND ACTIONS LAYER */}
            <div className="absolute inset-0 flex items-center justify-between px-10 z-0">
                <div 
                    className="flex flex-col items-center justify-center text-blue-500 transition-all duration-200"
                    style={{ opacity: pinOpacity, transform: `scale(${0.5 + pinOpacity/2})` }}
                >
                    <div className="p-4 bg-white/20 backdrop-blur-md rounded-full mb-2">
                        <MapPinIcon className="w-10 h-10 text-white" />
                    </div>
                    <span className="font-black uppercase tracking-widest text-white text-sm drop-shadow-md">
                        {isPinned ? 'Unpin' : 'Save'}
                    </span>
                </div>

                <div 
                    className="flex flex-col items-center justify-center text-red-500 transition-all duration-200"
                    style={{ opacity: hideOpacity, transform: `scale(${0.5 + hideOpacity/2})` }}
                >
                    <div className="p-4 bg-white/20 backdrop-blur-md rounded-full mb-2">
                        <EyeSlashIcon className="w-10 h-10 text-white" />
                    </div>
                    <span className="font-black uppercase tracking-widest text-white text-sm drop-shadow-md">
                        Hide
                    </span>
                </div>
            </div>

            {/* FOREGROUND CARD */}
            <div 
                className="absolute inset-0 bg-slate-900 z-10 transition-transform duration-200 ease-out origin-bottom"
                style={{ 
                    transform: `translateX(${offset}px) rotate(${rotate}deg) scale(${scale})`,
                    borderRadius: Math.abs(offset) > 0 ? '24px' : '0px'
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={handleClick}
            >
                <div className={`absolute inset-0 bg-gradient-to-br ${getGradientColors()}`}>
                    
                    {shouldLoad && imgSrc && !imgError && (
                        <img 
                            src={imgSrc} 
                            alt="Background" 
                            className="w-full h-full object-cover transition-opacity duration-500"
                            onError={handleImgError}
                            loading="lazy"
                        />
                    )}

                    {(!imgSrc || imgError || !shouldLoad) && (
                         <div className="absolute inset-0 opacity-10" style={{ 
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px' 
                         }}></div>
                    )}
                    
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent via-50% to-black/95"></div>
                </div>

                {/* Audio Player Overlay */}
                {topic.audioUrl && (
                    <div className="absolute bottom-32 md:bottom-20 left-6 right-6 z-20 pointer-events-auto">
                        <div className="bg-black/60 backdrop-blur-md rounded-2xl p-4 border border-white/20 shadow-xl">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isPlaying) {
                                            audioRef.current?.pause();
                                        } else {
                                            audioRef.current?.play();
                                        }
                                        setIsPlaying(!isPlaying);
                                    }}
                                    className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                                >
                                    {isPlaying ? (
                                        <div className="w-4 h-4 flex space-x-1">
                                            <div className="w-1 h-4 bg-white rounded-full"></div>
                                            <div className="w-1 h-4 bg-white rounded-full"></div>
                                        </div>
                                    ) : (
                                        <div className="w-0 h-0 border-l-8 border-l-white border-t-6 border-t-transparent border-b-6 border-b-transparent ml-1"></div>
                                    )}
                                </button>

                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-bold text-white uppercase tracking-widest">
                                            Audio Story
                                        </span>
                                        <SpeakerWaveIcon className="w-4 h-4 text-blue-400" />
                                    </div>
                                    <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 rounded-full w-0 transition-all duration-300"
                                            style={{width: isPlaying ? '100%' : '0%'}}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            <audio
                                ref={audioRef}
                                src={topic.audioUrl}
                                onEnded={() => setIsPlaying(false)}
                                onPause={() => setIsPlaying(false)}
                                onPlay={() => setIsPlaying(true)}
                                className="hidden"
                            />
                        </div>
                    </div>
                )}

                {/* Bottom Content Overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 pb-24 md:pb-8 text-white pointer-events-none">
                    
                    {/* Meta Badges */}
                    <div className="flex items-center space-x-2 mb-3 pointer-events-auto">
                        <span className="bg-blue-600/80 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10 shadow-sm">
                            {topic.category || 'News'}
                        </span>
                        {topic.region?.countryCode && (
                            <span className="bg-black/40 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center border border-white/10">
                                <GlobeAmericasIcon className="w-3 h-3 mr-1 text-blue-400" />
                                {topic.region.countryCode}
                            </span>
                        )}
                        {isPinned && (
                            <span className="bg-yellow-500/90 text-slate-900 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center shadow-lg">
                                <MapPinIcon className="w-3 h-3 mr-1" /> Saved
                            </span>
                        )}
                    </div>

                    {/* Headline & Summary */}
                    <div className="space-y-2 mb-6 pointer-events-auto">
                        <h2 className="text-2xl md:text-4xl font-black leading-tight tracking-tight drop-shadow-lg text-white">
                            {topic.title}
                        </h2>
                        <p className="text-sm md:text-lg font-medium text-slate-200 line-clamp-3 leading-relaxed opacity-90 drop-shadow-md">
                            {topic.summary}
                        </p>
                    </div>

                    {/* Footer Stats & CTA */}
                    <div className="flex items-center justify-between border-t border-white/20 pt-4 pointer-events-auto">
                        <div className="flex items-center space-x-4 text-xs font-bold text-slate-300">
                            <div className="flex items-center">
                                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1.5 text-yellow-400" />
                                {topic.postCount || 0}
                            </div>
                            <div className="flex items-center">
                                <ClockIcon className="w-4 h-4 mr-1.5 text-white" />
                                {topic.createdAt?.seconds 
                                    ? formatDistanceToNow(new Date(topic.createdAt.seconds * 1000), { addSuffix: true }) 
                                    : 'Just now'}
                            </div>
                        </div>
                        
                        <div className="flex items-center text-xs font-black uppercase tracking-widest text-white bg-white/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 animate-pulse">
                            Tap to Debate <ArrowRightIcon className="w-4 h-4 ml-2" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MAIN FEED CONTAINER ---
const NewsReel = ({ 
    topics, 
    onTopicClick, 
    onPin, 
    onHide, 
    pinnedIds,
    onMenuClick,
    onToggleView,
    onFilterClick
}) => {
    // TRACK CURRENT INDEX FOR LAZY LOADING
    const [activeIndex, setActiveIndex] = useState(0);

    const handleScroll = (e) => {
        const index = Math.round(e.target.scrollTop / e.target.clientHeight);
        setActiveIndex(index);
    };
    
    if (!topics || topics.length === 0) {
        return (
            <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-900 text-slate-500">
                <div className="absolute top-0 left-0 right-0 p-4 pt-12 flex justify-between">
                     <button onClick={onMenuClick} className="p-2 bg-white/10 rounded-full"><Bars3Icon className="w-6 h-6 text-white"/></button>
                     <button onClick={onToggleView} className="p-2 bg-white text-slate-900 rounded-full"><Squares2X2Icon className="w-6 h-6"/></button>
                </div>
                <div className="text-center p-8">
                    <p className="font-bold mb-2 text-xl text-white">All Caught Up</p>
                    <p className="text-sm">No active stories in this feed.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-black">
            <div className="absolute top-0 left-0 right-0 p-4 pt-12 flex justify-between items-start z-50 pointer-events-none">
                <div className="flex items-center gap-3 pointer-events-auto">
                    <button 
                        onClick={onMenuClick}
                        className="p-2 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/50 transition-colors border border-white/10"
                    >
                        <Bars3Icon className="w-6 h-6" />
                    </button>
                    <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg border border-white/20">
                        <span className="text-white font-black text-xl">V</span>
                    </div>
                </div>

                <div className="flex items-center gap-3 pointer-events-auto">
                    <button 
                        onClick={onFilterClick}
                        className="p-2 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/50 transition-colors border border-white/10"
                        title="Filter Topics"
                    >
                        <FunnelIcon className="w-6 h-6" />
                    </button>
                    <button 
                        onClick={onToggleView}
                        className="p-2 bg-white text-slate-900 rounded-full hover:bg-slate-200 transition-colors shadow-lg"
                        title="Switch to List View"
                    >
                        <Squares2X2Icon className="w-6 h-6" />
                    </button>
                </div>
            </div>

            <div 
                className="w-full h-full overflow-y-scroll snap-y snap-mandatory hide-scrollbar"
                onScroll={handleScroll} 
            >
                {topics.map((topic, index) => (
                    <ReelCard 
                        key={topic.id}
                        topic={topic}
                        isPinned={pinnedIds.includes(topic.id)}
                        onTopicClick={onTopicClick}
                        onPin={onPin}
                        onHide={onHide}
                        shouldLoad={Math.abs(index - activeIndex) <= 1} 
                    />
                ))}
                
                <div className="w-full h-full snap-start flex items-center justify-center bg-slate-900 text-white">
                    <div className="text-center p-8">
                        <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                            <ArrowRightIcon className="w-10 h-10 text-white rotate-[-90deg]" />
                        </div>
                        <h3 className="text-2xl font-black uppercase tracking-widest mb-3">End of Feed</h3>
                        <p className="text-slate-400 font-medium">Swipe up to refresh or check back later.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsReel;